import * as React from "react";
import { resolveImageSources, DEFAULT_WIDTHS } from "@/lib/image-sources";

type ResponsiveImageProps = Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  "src" | "srcSet"
> & {
  src: string;
  /** Required: descriptive alt text (empty string only for decorative art). */
  alt: string;
  /** How wide the image actually renders, e.g. "(min-width: 640px) 50vw, 100vw". */
  sizes?: string;
  /** Candidate widths to generate/request. */
  widths?: readonly number[];
  /** LCP image: eager + high fetch priority. */
  priority?: boolean;
  pictureClassName?: string;
};

/**
 * Renders a `<picture>` with AVIF / WebP / original sources so the browser
 * downloads the smallest file that is still sharp on that screen.
 */
export const ResponsiveImage = React.forwardRef<HTMLImageElement, ResponsiveImageProps>(
  function ResponsiveImage(
    { src, alt, sizes, widths = DEFAULT_WIDTHS, priority = false, pictureClassName, ...imgProps },
    ref,
  ) {
    const w = Number(imgProps.width);
    const h = Number(imgProps.height);
    const aspect = w > 0 && h > 0 ? w / h : undefined;
    const sources = resolveImageSources(src, widths, aspect);
    const loading = priority ? "eager" : (imgProps.loading ?? "lazy");

    const img = (
      <img
        {...imgProps}
        ref={ref}
        src={sources.src}
        srcSet={sources.srcSet}
        sizes={sources.srcSet || sources.avifSrcSet || sources.webpSrcSet ? sizes : undefined}
        alt={alt}
        loading={loading}
        decoding={priority ? "sync" : "async"}
        {...(priority ? { fetchPriority: "high" as const } : {})}
      />
    );

    if (!sources.avifSrcSet && !sources.webpSrcSet) return img;

    return (
      <picture className={pictureClassName}>
        {sources.avifSrcSet && <source type="image/avif" srcSet={sources.avifSrcSet} sizes={sizes} />}
        {sources.webpSrcSet && <source type="image/webp" srcSet={sources.webpSrcSet} sizes={sizes} />}
        {img}
      </picture>
    );
  },
);
